/**
 * routeLabel(pathname) — a readable breadcrumb label for any app route.
 *
 * Used by the global navigation-journey as a FALLBACK when a page hasn't
 * registered an explicit label via useJourneyNode (detail pages still override
 * with their dynamic name, e.g. the actual project/vendor name). Pure + testable.
 */
const STATIC = {
  '': 'Dashboard', dashboard: 'Dashboard',
  'account-dashboard': 'Account Dashboard', 'cxo-dashboard': 'CXO Dashboard',
  'cfo-dashboard': 'CFO Dashboard', 'md-dashboard': 'MD Dashboard',
  'vendor-dashboard': 'Vendor Dashboard', 'driver-dashboard': 'Driver Dashboard',
  'client-servicing-dashboard': 'Client Servicing', 'campaign-driver-dashboard': 'Campaign Driver',
  'purchase-dashboard': 'Purchase Dashboard', 'business-intelligence': 'Business Intelligence',
  'operations-members': 'Operations Members', 'campaign-inventory': 'Campaign Inventory',
  'campaign-inventory-manager': 'Campaign Inventory', 'campaign-reports': 'Campaign Reports',
  'relationship-map': 'Relationship Map', 'daily-reporting': 'Daily Reporting',
  'promoter-activities': 'Daily Reporting', operations: 'Operations',
  projects: 'Projects', campaigns: 'Campaigns', vendors: 'Vendors', clients: 'Clients',
  vehicles: 'Vehicles', drivers: 'Drivers', promoters: 'Promoters', anchors: 'Anchors',
  orders: 'Orders', tasks: 'Tasks', expenses: 'Expenses',
  invoices: 'Invoices', payments: 'Payments', reports: 'Reports', 'report-templates': 'Report Templates',
  analytics: 'Analytics', settings: 'Settings', accounts: 'Accounts',
  'warehouse-inventory': 'Warehouse Inventory', 'godown-inventory': 'Godown Inventory',
  'godown-items': 'Godown Items', 'client-pos': 'Client POs', 'van-expense': 'Van Expense',
  'damaged-items': 'Damaged Items', godowns: 'Godowns', journey: 'Journey',
};
const SINGULAR = {
  projects: 'Project', campaigns: 'Campaign', vendors: 'Vendor', clients: 'Client',
  vehicles: 'Vehicle', drivers: 'Driver', promoters: 'Promoter', anchors: 'Anchor',
  tasks: 'Task', orders: 'Order', invoices: 'Invoice', payments: 'Payment',
  reports: 'Report', expenses: 'Expense', 'godown-items': 'Godown Item', godowns: 'Godown',
};
const EXACT = {
  'orders/reconciliation': 'Order Reconciliation', 'orders/approvals': 'Order Approvals',
  'analytics/profitability': 'Project Profitability',
  'purchase/requests': 'Purchase Requests', 'purchase/rfqs': 'RFQs', 'purchase/orders': 'Purchase Orders',
  'purchase/po-payments': 'PO Payments', 'purchase/expense-approvals': 'Expense Approvals',
  'accounts/payment-requests': 'Payment Requests', 'accounts/advances': 'Advances',
  'accounts/payment-allocations': 'Bill-wise Adjustment', 'accounts/overheads': 'Overheads',
  'accounts/payments-tracker': 'Payments Tracker', 'accounts/billing-corrections': 'Billing Corrections',
  'settings/user-management': 'User Management', 'settings/user-registration': 'User Registration',
  'settings/roles': 'Roles', 'settings/master-data': 'Master Data', 'settings/system': 'System Settings',
  'settings/integrations': 'Integrations',
};

export function prettify(s) {
  return String(s || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const isId = (s) => /^\d+$/.test(s) || s === 'me' || s === 'new';

export function routeLabel(pathname) {
  const segs = String(pathname || '/').split('/').filter(Boolean);
  if (segs.length === 0) return 'Dashboard';
  const joined = segs.join('/');
  if (EXACT[joined]) return EXACT[joined];

  // purchase/<coll>/<id|new>
  if (segs[0] === 'purchase' && segs.length >= 3 && isId(segs[2])) {
    const sing = { requests: 'Purchase Request', rfqs: 'RFQ', orders: 'Purchase Order' }[segs[1]] || 'Purchase';
    return segs[2] === 'new' ? `New ${sing}` : sing;
  }
  // relationship-map/<entityType>/<id>
  if (segs[0] === 'relationship-map' && segs.length >= 2) return 'Relationship Map';
  if (segs[0] === 'van-expense') return segs.length > 1 ? prettify(segs[1]) : 'Van Expense';

  const coll = segs[0];
  const sing = SINGULAR[coll] || STATIC[coll] || prettify(coll);
  if (segs.length >= 2) {
    if (segs[1] === 'new') return `New ${sing}`;
    if (isId(segs[1])) {
      if (segs[2] === 'edit') return `Edit ${sing}`;
      if (segs[2] === 'setup') return `${sing} Setup`;
      if (segs[2] === 'inventory') return `${sing} Inventory`;
      return sing;
    }
    // two static segments not in EXACT → prettify the last
    return prettify(segs[segs.length - 1]);
  }
  return STATIC[coll] || prettify(coll);
}

export default routeLabel;
